/**
 * 파일 변환 관련 API
 */

import { apiCall } from "./base";

const CANONICAL_STORAGE_URI_PREFIXES = ["gs://", "s3://"];

export type StartFileConversionParams = {
  dev_extra_args?: string;
  job_id?: string | null;
  file_path?: string | null;
  viewr_gcs_path?: string | null;
  project_id?: string | number | null;
  base_revision_id?: string | null;
  vdb_gcs_path?: string | null;
  space_index?: number | null;
  vse_index?: number | null;
};

type StartFileConversionPayload = {
  dev_extra_args?: string;
  job_id?: string;
  file_path?: string;
  viewr_gcs_path?: string;
  project_id?: number;
  base_revision_id?: string;
  vdb_gcs_path?: string;
  space_index?: number;
  vse_index?: number;
};

const normalizeOptionalString = (
  value: string | null | undefined,
): string | undefined => {
  if (value == null) return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

const normalizeHybridIndex = (
  value: number | null | undefined,
  fieldName: "space_index" | "vse_index",
): number | undefined => {
  if (value == null) return undefined;
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${fieldName} must be a non-negative number.`);
  }
  return Math.trunc(value);
};

const isCanonicalStorageUri = (value: string): boolean =>
  CANONICAL_STORAGE_URI_PREFIXES.some((prefix) => value.startsWith(prefix));

export const buildStartFileConversionPayload = (
  params: StartFileConversionParams,
): StartFileConversionPayload => {
  const payload: StartFileConversionPayload = {};
  if (params.dev_extra_args !== undefined) {
    if (params.dev_extra_args.length > 4096 || /[\x00-\x1f\x7f]/.test(params.dev_extra_args)) {
      throw new Error("Extra Args must be a single line of at most 4096 characters.");
    }
    payload.dev_extra_args = params.dev_extra_args.trim();
  }
  const jobId = normalizeOptionalString(params.job_id);
  const filePath = normalizeOptionalString(params.file_path);

  if (jobId) {
    payload.job_id = jobId;
  } else if (filePath) {
    payload.file_path = filePath;
  } else {
    throw new Error("job_id or file_path is required.");
  }

  const viewrGcsPath = normalizeOptionalString(params.viewr_gcs_path);
  const baseRevisionId = normalizeOptionalString(params.base_revision_id);
  const projectId =
    params.project_id == null || params.project_id === ""
      ? undefined
      : Number(params.project_id);
  if (
    projectId !== undefined &&
    (!Number.isInteger(projectId) || projectId <= 0)
  ) {
    throw new Error("project_id must be a positive integer.");
  }
  const vdbGcsPath = normalizeOptionalString(params.vdb_gcs_path);
  const spaceIndex = normalizeHybridIndex(params.space_index, "space_index");
  const vseIndex = normalizeHybridIndex(params.vse_index, "vse_index");
  const hasLegacyBase = viewrGcsPath != null;
  const hasRevisionBase = projectId != null || baseRevisionId != null;
  const commonValues = [vdbGcsPath, spaceIndex, vseIndex];
  const commonPresent = commonValues.filter((value) => value != null).length;

  if (hasLegacyBase || hasRevisionBase || commonPresent > 0) {
    if (hasLegacyBase && hasRevisionBase) {
      throw new Error("create-vse hybrid accepts only one project base identity.");
    }
    if (!hasLegacyBase && !hasRevisionBase) {
      throw new Error("create-vse hybrid requires a project base identity.");
    }
    if (commonPresent !== 3) {
      throw new Error(
        "create-vse hybrid requires a project base, vdb_gcs_path, space_index, and vse_index together.",
      );
    }
    if (hasLegacyBase && !isCanonicalStorageUri(viewrGcsPath)) {
      throw new Error("viewr_gcs_path must start with gs:// or s3://.");
    }
    if (!isCanonicalStorageUri(vdbGcsPath)) {
      throw new Error("vdb_gcs_path must start with gs:// or s3://.");
    }
    if (hasRevisionBase) {
      if (projectId == null || !baseRevisionId) {
        throw new Error(
          "revision hybrid requires project_id and base_revision_id together.",
        );
      }
      if (!jobId) {
        throw new Error("revision hybrid requires job_id.");
      }
      payload.project_id = projectId;
      payload.base_revision_id = baseRevisionId;
    } else {
      payload.viewr_gcs_path = viewrGcsPath;
    }
    payload.vdb_gcs_path = vdbGcsPath;
    payload.space_index = spaceIndex;
    payload.vse_index = vseIndex;
  }

  return payload;
};

/**
 * 파일 변환 시작
 * @param params - job_id (임시 업로드 플로우) 또는 file_path (레거시) 중 하나 필수
 * 변환 인자는 Main API가 환경별 기능 설정에서 적용한다.
 */
export const startFileConversion = async (
  params: StartFileConversionParams,
  userToken: string | null,
): Promise<unknown> => {
  const payload = buildStartFileConversionPayload(params);
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/raw-files/convert", options);
};

/**
 * 파일 변환 API 호출
 */
export const convertFile = async (fileData: unknown): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(fileData),
    headers: {
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/convert-file", options);
};

/**
 * 파일 변환 상태 확인
 */
export const getConvertStatus = async (): Promise<unknown> => {
  return await apiCall("/convert-status");
};

/**
 * 파일 변환 작업 요청
 */
export const convertFileJob = async (
  jobId: string,
  outputFormat = "fbx"
): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify({ output_format: outputFormat }),
    headers: {
      "Content-Type": "application/json",
    },
  };

  return await apiCall(`/convert-file-job?job_id=${jobId}`, options);
};

/**
 * 변환 작업 상태 조회
 */
export const getConversionJobStatus = async (
  jobId: string,
  userToken: string | null
): Promise<unknown> => {
  const headers: Record<string, string> = {};
  if (userToken) {
    headers["Authorization"] = `Bearer ${userToken}`;
  }

  const result = await apiCall(`/conversion-job/${jobId}`, {
    headers,
  });
  return result;
};

/**
 * 사용자의 모든 변환 작업 조회
 */
export const getUserConversionJobs = async (): Promise<unknown> => {
  return await apiCall("/api/jobs/conversion-jobs");
};

/**
 * 사용자 변환 작업 목록 조회
 */
export const getConversionJobs = async (
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return await apiCall("/api/jobs/conversion-jobs", options);
};

/**
 * 변환 작업 진행도 업데이트 (Compute Engine에서 호출)
 */
export const updateConversionProgress = async (
  jobId: string,
  progress: number,
  status: string | null = null,
  convertedFilePath: string | null = null,
  errorMessage: string | null = null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "PUT",
    body: JSON.stringify({
      progress,
      status,
      converted_file_path: convertedFilePath,
      error_message: errorMessage,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  };

  return await apiCall(
    `/api/jobs/conversion-job/${jobId}/progress`,
    options
  );
};

/**
 * Cloud Storage 기반 파일 변환 요청
 */
export const requestFileConversion = async (fileData: {
  downloadURL: string;
  filename: string;
  originalFilename: string;
  outputFormat?: string;
}): Promise<unknown> => {
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify({
      download_url: fileData.downloadURL,
      filename: fileData.filename,
      original_filename: fileData.originalFilename,
      output_format: fileData.outputFormat || "fbx",
    }),
    headers: {
      "Content-Type": "application/json",
    },
  };

  return await apiCall("/request-storage-conversion", options);
};

/**
 * convert 버킷 파일 서명 URL 발급
 */
export const getConvertDownloadUrl = async (
  filePath: string,
  userToken: string | null
): Promise<unknown> => {
  const payload = { file_path: filePath };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/convert-files/generate-download-url", options);
};
